import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmt } from "@/lib/api";
import { CriteriaBlock, cardTotals } from "@/pages/ScoreCard";
import { PenaltyRow } from "@/components/fly/PenaltyRow";

export const ScoreEditDialog = ({ edit, perf, onClose, onSaved }) => {
  const [card, setCard] = useState(null);
  const [vals, setVals] = useState([]);
  const [pen, setPen] = useState({});
  const [note, setNote] = useState("");
  const [showMissing, setShowMissing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!edit) return;
    setCard(null);
    api.get(`/admin/performances/${perf.id}/card/${edit.judge}`).then(({ data: c }) => {
      const s = edit.score;
      setCard(c);
      setVals(s ? s.sections.map((x) => x.values) : c.sections.map((x) => Array(x.criteria.length).fill(null)));
      setPen(Object.fromEntries(c.penalties.map((p) => [p.key, s?.penalties?.[p.key] || 0])));
      setNote(s?.note || "");
      setShowMissing(false);
    }).catch((e) => toast.error(errMsg(e)));
  }, [edit, perf]);

  const totals = useMemo(() => cardTotals(card, vals, pen), [card, vals, pen]);
  const setAt = (si) => (i) => (v) => setVals((all) => all.map((arr, k) => (k === si ? arr.map((x, j) => (j === i ? v : x)) : arr)));

  const save = async () => {
    if (vals.flat().some((x) => x == null)) {
      setShowMissing(true);
      toast.error("Uzupełnij wszystkie kryteria");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/performances/${perf.id}/scores/${edit.judge}`, { values: vals, penalties: pen, note });
      toast.success(`Zapisano ocenę sędziego ${edit.judge}: ${fmt(totals.total)} pkt`);
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!edit} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0B132B] border-blue-500/30 w-[95vw] max-w-5xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="score-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase">{edit?.score ? "Edycja oceny" : "Nowa ocena"} – sędzia {edit?.judge}</DialogTitle>
          <p className="text-xs font-mono text-blue-400">{perf?.name} · {perf?.category}{card && ` · karta: ${card.name}`}</p>
        </DialogHeader>
        {edit && !card && <Loader2 className="w-6 h-6 animate-spin text-blue-500" />}
        {edit && card && (
          <div className="grid lg:grid-cols-2 gap-4">
            {card.sections.map((s, si) => (
              <CriteriaBlock key={si} title={s.name} max={s.criteria.length * 10} names={s.criteria} values={vals[si] || []} onSet={setAt(si)} prefix={`edit-sec${si}`} sum={totals.secs[si] || 0} required showMissing={showMissing} />
            ))}
            <section className="fly-card p-5 lg:col-span-2">
              {card.penalties.length > 0 && <h3 className="font-display text-xl font-bold uppercase">Odjęcia</h3>}
              <div className="grid md:grid-cols-2 gap-x-8">
                {card.penalties.map((d) => <PenaltyRow key={d.key} def={d} value={pen[d.key] || 0} onChange={(v) => setPen((p) => ({ ...p, [d.key]: v }))} />)}
              </div>
              <textarea data-testid="edit-note-input" value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} placeholder="Komentarz sędziego"
                className="mt-4 w-full min-h-[80px] rounded-xl bg-[#0B132B] border border-blue-500/20 focus:border-blue-500 outline-none p-3 text-sm" />
            </section>
            <div className="lg:col-span-2 flex items-center justify-between gap-3">
              <span data-testid="edit-total" className="font-display text-3xl font-black">{fmt(totals.total)} pkt</span>
              <button data-testid="edit-save-btn" disabled={busy} onClick={save} className="h-12 px-6 rounded-xl bg-blue-600 hover:bg-blue-500 font-semibold flex items-center gap-2 disabled:opacity-50">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}Zapisz ocenę
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
